import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormCheckbox,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateIngredientSimpleDetailsMutation } from '../../hooks'
import { ingredientPropType } from '../../prop-types'
import { setIngredientPageIsDirty } from '../../redux/actions'

import { ingredientSimpleDetailsFormInitialValues } from './initial-values'

const IngredientSimpleDetailsForm = ({ ingredient, maxPosition }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.INGREDIENT.UPDATE_SIMPLE_DETAILS,
  ])

  const [
    updateIngredientSimpleDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateIngredientSimpleDetailsMutation()

  const initialValues = useMemo(
    () => ingredientSimpleDetailsFormInitialValues(ingredient),
    [ingredient],
  )

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const handleSaveData = useCallback(
    data => updateIngredientSimpleDetails(ingredient.id, data),
    [ingredient.id, updateIngredientSimpleDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Ingredient details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [
    initialValues.isExcludedFromSearch,
    initialValues.position,
    initialValues.description,
    resetForm,
  ])

  useEffect(() => {
    dispatch(setIngredientPageIsDirty({ isSimpleDetailsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editIngredient"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{ingredient.name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
        </Col>
        <Col span={12}>
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
        <Col span={12}>
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app search"
            name="isExcludedFromSearch"
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.INGREDIENT.UPDATE_SIMPLE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

IngredientSimpleDetailsForm.propTypes = {
  ingredient: ingredientPropType.isRequired,
  maxPosition: PropTypes.number.isRequired,
}

export default IngredientSimpleDetailsForm
